import { useEditorStore } from "@/store";
import type { EditorMode } from "@/types/editor";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { JsonEditor } from "./json/JsonEditor";
import { SectionsPanel } from "./sections/SectionsPanel";
import { LayerStack } from "./layerstack/LayerStack";

const TABS: { id: EditorMode; label: string }[] = [
  { id: "sections", label: "Editor" },
  { id: "json", label: "JSON" },
  { id: "layerstack", label: "Layers" },
];

const TAB_INFO: Record<EditorMode, React.ReactNode> = {
  sections: null, // Sections have their own per-section banners
  json: (
    <>
      Edit the raw style.json directly. Changes apply to the map in real-time as
      you type. Validation errors appear as red markers in the gutter.
      Use <kbd className="rounded bg-surface-sunken px-1 font-mono text-[10px]">Ctrl+F</kbd> to
      search, <kbd className="rounded bg-surface-sunken px-1 font-mono text-[10px]">Ctrl+Z</kbd> to
      undo.
    </>
  ),
  layerstack: (
    <>
      Visual overview of all layers and their zoom ranges. The vertical line
      shows the current map zoom. Colored bars indicate each layer's visible
      range — dim bars mean the layer is hidden or out of range. Click a layer
      to edit it, or toggle visibility with the dot icon.
    </>
  ),
};

export function EditorTabs() {
  const editorMode = useEditorStore((s) => s.editorMode);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface-base">
      {/* Tab bar */}
      <div className="flex shrink-0 border-b border-border-subtle bg-surface-raised">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setEditorMode(tab.id)}
            className={`px-4 py-2 text-xs font-medium transition-colors ${
              editorMode === tab.id
                ? "border-b-2 border-accent text-accent"
                : "text-text-muted hover:text-text-secondary"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab info banner */}
      {TAB_INFO[editorMode] && (
        <div className="shrink-0 border-b border-border-subtle px-3 py-2">
          <InfoBanner storageKey={`tab-${editorMode}`}>
            {TAB_INFO[editorMode]}
          </InfoBanner>
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {editorMode === "json" && (
          <div className="h-full p-2">
            <JsonEditor />
          </div>
        )}
        {editorMode === "sections" && <SectionsPanel />}
        {editorMode === "layerstack" && (
          <div className="h-full p-2">
            <LayerStack />
          </div>
        )}
      </div>
    </div>
  );
}
